import React, { FC } from "react";
import { AutoComplete, Button, Form } from "antd";
import TextArea from "antd/es/input/TextArea";
import { nanoid } from "nanoid";

import { useGetApplicationDataToBankQuery } from "../../../../../store/api/applicationBankApi/applicationBank.api";
import { useParams } from "react-router-dom";
import { ruleText } from "../../../../../constants/rules/RulesConstantsForms";
import { StopFactor } from "../../../../../store/types/scoring.type";
import { APPLICATION_BANK_STATUS } from "../../../../../utils/guards/ApplicationBankStatusGuard/types";
import { ColumnsType } from "antd/es/table";
import { AnyObject } from "antd/es/_util/type";
import { Table } from "antd/lib";
import { CommentForm } from "../../../../../UI_ITO/UI_ITOModuleCommentForm/CommentForm";
import { UI_ITOModuleSuccess } from "../../../../../UI_ITO/indexUI";
import { Guard } from "../../../../../utils/guards/Guard";

import classes from "./StopFactors.module.css";
import {
  StopFactorsChange,
  useChangeDataToScoringMutation,
} from "../../../../../store/api/applicationBankApi/scoring.api";

interface DataSourceType extends Omit<StopFactor, "result"> {
  result?: string[];
  number: string;
}

const columns = (disabled: boolean): ColumnsType<DataSourceType> => [
  {
    title: "№",
    dataIndex: "number",
    key: "number",
    render: (value: string, row) => (
      <Form.Item
        style={{ margin: 0, width: "100%", padding: 0 }}
        name={[row.key, "number"]}
        noStyle
      >
        {value}
      </Form.Item>
    ),
  },
  {
    title: "Проверяемая информация",
    dataIndex: "title",
    width: "50%",
    key: "title",
  },
  {
    title: "Результат скорринга",
    dataIndex: "result",
    width: "30%",
    render: (value: string[]) => {
      if (value?.length) {
        return value.map((item: string) => {
          const key = nanoid();
          return <p key={key}>{item}</p>;
        });
      } else {
        return "Нет данных";
      }
    },
    key: "result",
  },
  {
    title: "Комментарии",
    dataIndex: "comment",
    width: "20%",
    render: (_value: string, row: DataSourceType) => (
      <Form.Item
        style={{ margin: 0, width: "100%", padding: 0 }}
        name={[row.key, "comment"]}
        rules={[ruleText]}
      >
        <AutoComplete
          options={[{ value: "Да" }, { value: "Нет" }, { value: "Нет данных" }]}
        >
          <TextArea disabled={disabled} />
        </AutoComplete>
      </Form.Item>
    ),
    key: "comment",
  },
];

export const StopFactors: FC = () => {
  const { applicationBankId } = useParams();
  const { data: applicationBank } = useGetApplicationDataToBankQuery(
    Number(applicationBankId)
  );
  const [stopFactorsForm] = Form.useForm();
  const stopFactorsData = applicationBank?.scoring_result.data.stopFactors;
  const commentStopFactors =
    applicationBank?.scoring_result.data.commentStopFactors;

  const [changeScoringData, { isSuccess, isLoading }] =
    useChangeDataToScoringMutation();

  const dataSource = stopFactorsData?.map((record, index) => {
    return {
      ...record,
      // Преобразование объекта ответа в объект, где в поле result хранится массив строк –
      // чтобы передать данные в таблицу
      result: record.result?.flatMap((value) =>
        Object.values(value).map((data) => {
          return data;
        })
      ),
      comment: record.comment,
      number: record.key,
      key: index,
    };
  });

  const handleFinish = (
    values: Record<string, { comment?: string; number?: string }>
  ) => {
    const formData = Object.keys(values).map((item) => {
      return {
        key: values[item]?.number,
        comment: values[item]?.comment ?? "",
      };
    });
    formData.pop();

    const data = {
      type: "stopFactors",
      commentStopFactors: values["commentStopFactors"] ?? "",
      results: formData,
    };

    if (applicationBankId) {
      changeScoringData({
        app_id: +applicationBankId,
        data: { ...(data as StopFactorsChange) },
      });
    }
  };

  const initialValues = {
    ...dataSource,
    commentStopFactors: commentStopFactors,
  };

  return (
    <>
      <Form
        form={stopFactorsForm}
        name={"stopFactorsForm"}
        onFinish={handleFinish}
        initialValues={initialValues}
        autoComplete="off"
        scrollToFirstError={{ behavior: "smooth", block: "end" }}
      >
        {dataSource && (
          <Table
            style={{ whiteSpace: "pre-line" }}
            dataSource={dataSource}
            rowClassName={() => "editable-row"}
            bordered
            pagination={false}
            columns={
              columns(
                applicationBank?.modified_bank_status.type ===
                  APPLICATION_BANK_STATUS.NEW_APPLICATION
              ) as ColumnsType<AnyObject>
            }
          />
        )}

        <CommentForm
          name="commentStopFactors"
          disabled={
            applicationBank?.modified_bank_status.type ===
            APPLICATION_BANK_STATUS.NEW_APPLICATION
          }
        />

        <div className={classes.submitWrapper}>
          <UI_ITOModuleSuccess
            isSuccess={isSuccess}
            title="Данные успешно сохранены"
          />
          {!!applicationBank && (
            <Guard.ApplicationBankStatus
              status={applicationBank.modified_bank_status.type}
              disabledStatuses={[APPLICATION_BANK_STATUS.NEW_APPLICATION]}
            >
              <Button htmlType="submit" type="primary" loading={isLoading}>
                Сохранить
              </Button>
            </Guard.ApplicationBankStatus>
          )}
        </div>
      </Form>
    </>
  );
};
